import Vue from 'vue'
import { Component, Watch } from 'vue-property-decorator'

import { ApolloResponse } from '@/interfaces/apollo'
import { Breadcrumb } from '@/interfaces/breadcrumbs'
import { Snack, SnackType } from '@/interfaces/snack'
import { showErrors, showNotification } from '@/utils/snack'
import { dateTimeToDisplay, dateToDisplay } from '@/utils/dateTime'
import { tablePerPageOptions } from '@/utils/tablePerPageOptions'
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import AppCheckbox from '@/components/AppCheckbox.vue'
import AppSearch from '@/components/AppSearch.vue'
import AppDialog from '@/components/AppDialog.vue'
import AppTextField from '@/components/AppTextField.vue'
import AppTextarea from '@/components/AppTextarea.vue'
import AppSelector from '@/components/AppSelector.vue'
import AppDatePicker from '@/components/AppDatePicker.vue'
import AppTableDate from '@/components/AppTableDate.vue'
import { getLIAs, newLIA } from '@/modules/LifeInsure/gql/life-insure.gql'
import AppPhoneInput from '@/components/AppPhoneInput.vue'
import delNonNumCode from '@/utils/delNonNumCode'

@Component({
  components: {
    AppBreadcrumbs,
    AppCheckbox,
    AppSearch,
    AppDialog,
    AppTextField,
    AppTextarea,
    AppSelector,
    AppDatePicker,
    AppTableDate,
    AppPhoneInput
  }
})

export default class LifeInsureCaseList extends Vue {
  breadcrumbs: Breadcrumb[] = [
    {
      text: 'Life Insure Case'
    }
  ]

  onlyApplied = false

  searchVal = ''

  newCaseInput = {
    name: '',
    gender: -1,
    birthday: '',
    phoneSplice: '',
    memo: ''
  }

  genderOptions = ['Male', 'Female', 'Other']

  newCaseValid = true

  disableConfirm = false

  tableColumn = [
    { field: 'id', title: 'Case ID', sortable: true, resizable: true },
    { field: 'name', title: 'Name', slots: { default: 'name_default' }, sortable: true, resizable: true },
    { field: 'applicationForm', title: '申请表', slots: { default: 'applicationForm_default' }, resizable: true },
    { field: 'financialProof', title: '财务证明', slots: { default: 'financialProof_default' }, resizable: true },
    { field: 'examinationTable', title: '体检表', slots: { default: 'examinationTable_default' }, resizable: true },
    { field: 'bankCheque', title: '银行支票', slots: { default: 'bankCheque_default' }, resizable: true },
    { field: 'AmericanRelationsProof', title: '美国关系证明', slots: { default: 'AmericanRelationsProof_default' }, resizable: true },
    { field: 'updatedAt', title: 'Last Modified', sortable: true, resizable: true, slots: { default: 'updated_at' }, width: 150 },
    { field: 'agent', title: 'Agent', sortable: true, resizable: true },
    { field: 'applied', title: 'Applied', slots: { default: 'applied_default' }, width: 102 }
  ]

  tableOptions = {
    loading: false,
    tablePage: tablePerPageOptions(),
    sortBy: 'name',
    sortDesc: true
  }

  cases = []

  windowSize = {
    w: 0,
    h: 0
  }

  orderBy: {
    [key: string]: string
  } = {
    id: 'ID',
    name: 'NAME',
    agent: 'APPLICANT_NAME',
    updatedAt: 'UPDATED_AT',
    applied: 'APPLIED'
  }

  newCaseDialog = false

  dateToDisplay = dateToDisplay
  dateTimeToDisplay = dateTimeToDisplay

  mounted () {
    this.onResize()
    this.getCases()
  }

  @Watch('onlyApplied')
  onOnlyAppliedChanged (val: boolean, oldVal: boolean) {
    this.getCases()
  }

  async getCases () {
    const { sortBy, sortDesc, tablePage } = this.tableOptions
    const { currentPage, pageSize } = tablePage
    const searchVal = this.searchVal ? this.searchVal : null
    const orderBy = this.orderBy[sortBy] || 'ID'
    this.tableOptions.loading = true
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getLIAs,
        variables: {
          filter: {
            onlyApplied: this.onlyApplied,
            name: searchVal
          },
          order: {
            orderBy: orderBy,
            asc: !sortDesc
          },
          paging: {
            offset: (currentPage - 1) * pageSize,
            limit: pageSize
          }
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      const liaCases = result.data.getLIAs.lias.map((liaCase: any) => {
        const files = this.$R.groupBy((file: any) => {
          return file.type
        })(liaCase.files)
        return this.$R.merge(liaCase, {
          agent: liaCase.applicantName,
          applicationForm: this.$R.has('EXAMINATION_TABLE')(files),
          financialProof: this.$R.has('FINANCIAL_PROOF')(files),
          examinationTable: this.$R.has('EXAMINATION_TABLE')(files),
          bankCheque: this.$R.has('BANK_CHEQUE')(files),
          AmericanRelationsProof: this.$R.has('AMERICAN_RELATIONS_PROOF')(files),
          updatedAt: liaCase.updatedAt
        })
      })
      this.tableOptions.loading = false
      this.cases = liaCases
      this.tableOptions.tablePage.total = result.data.getLIAs.paging.totalCount
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  onResize () {
    this.windowSize = { w: window.innerWidth, h: window.innerHeight }
  }

  async newCaseConfirm () {
    const newCaseValid = (this.$refs.form as Vue & { validate: () => boolean }).validate()
    if (!newCaseValid) {
      return
    }

    const gender = this.genderOptions[this.newCaseInput.gender]

    const phoneList = this.newCaseInput.phoneSplice.split(',')
    const countryCode = phoneList[0] ? phoneList[0] : ''
    const phone = phoneList[1] ? phoneList[1] : ''
    const extensionNumber = phoneList[2] ? phoneList[2] : ''
    const countryCallingCode = phoneList[3] ? phoneList[3] : ''

    if (!phone) {
      return
    }
    this.disableConfirm = true
    try {
      const result = await this.$apollo.mutate({
        mutation: newLIA,
        variables: {
          liaInput: {
            name: this.newCaseInput.name,
            birthday: this.newCaseInput.birthday,
            phone: delNonNumCode(phone),
            gender: gender,
            memo: this.newCaseInput.memo,
            countryCode,
            extensionNumber,
            countryCallingCode
          }
        }
      })
      showNotification({
        msg: 'New case created successfully',
        type: SnackType.Success
      })
      await this.getCases()
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
    this.newCaseDialog = false
    this.disableConfirm = false
    this.clearNewCase()
  }

  clearNewCase () {
    this.newCaseInput = {
      name: '',
      gender: -1,
      birthday: '',
      phoneSplice: '',
      memo: ''
    }
  }

  routeToDetail (item: any) {
    return {
      name: 'LifeInsureCaseDetail',
      params: {
        id: item.id
      }
    }
  }

  sortTable (column: any) {
    this.tableOptions.sortBy = column.property
    this.tableOptions.sortDesc = column.order === 'desc'
    this.getCases()
  }

  handlePageChange (pageData: any) {
    this.tableOptions.tablePage.currentPage = pageData.currentPage
    this.tableOptions.tablePage.pageSize = pageData.pageSize
    this.getCases()
  }
}
